/**
 * EquipmentDetailPanel — Expanded detail for a selected equipment item
 * Shows description, full stats, slot info, and equipped status
 */

import { EquipmentDefinition } from '@/types';
import { SLOT_CONFIG } from '@/data/equipment/slotConfig';

interface EquipmentDetailPanelProps {
  definition: EquipmentDefinition;
  isEquipped: boolean;
  quantity: number;
  onClose: () => void;
}

export function EquipmentDetailPanel({
  definition,
  isEquipped,
  quantity,
  onClose,
}: EquipmentDetailPanelProps) {
  const slotMeta = SLOT_CONFIG[definition.slot];

  return (
    <div className="col-span-full bg-gray-700/50 rounded-lg p-3 border border-gray-600 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{definition.icon || slotMeta.icon}</span>
          <span className="text-sm font-bold text-white">{definition.name}</span>
          <span className="text-xs text-gray-400">
            {slotMeta.label} T{definition.tier}
          </span>
          <span className="text-xs text-gray-400">×{quantity}</span>
          {isEquipped && (
            <span className="text-xs text-green-400 font-semibold">EQ</span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close equipment detail"
          className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-600 transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Description */}
      {definition.description && (
        <p className="text-xs text-gray-300">{definition.description}</p>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs">
        {definition.stats.fishingSpeed ? (
          <span className="text-green-400">
            Speed +{Math.round(definition.stats.fishingSpeed * 100)}%
          </span>
        ) : null}
        {definition.stats.catchChance ? (
          <span className="text-blue-400">
            Catch +{Math.round(definition.stats.catchChance * 100)}%
          </span>
        ) : null}
        {definition.stats.qualityBonus ? (
          <span className="text-purple-400">
            Quality +{Math.round(definition.stats.qualityBonus * 100)}%
          </span>
        ) : null}
        {definition.stats.valueBonus ? (
          <span className="text-yellow-400">
            Value +{Math.round(definition.stats.valueBonus * 100)}%
          </span>
        ) : null}
        {!definition.stats.fishingSpeed &&
          !definition.stats.catchChance &&
          !definition.stats.qualityBonus &&
          !definition.stats.valueBonus && (
            <span className="text-gray-500">No stat bonuses</span>
          )}
      </div>

      {/* Meta */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        {definition.cost > 0 && <span>Cost: {definition.cost}g</span>}
        {definition.isConsumable && <span>Consumable</span>}
        {definition.tackleType && (
          <span>Type: {definition.tackleType}</span>
        )}
      </div>
    </div>
  );
}
